import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { useHrisLocationSidebarQuery } from "./HrisLocationDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import SidebarLink from "@src/features/sidebars/ui/SidebarLink";
import ClickableLinkText from "@src/lib/ui/ClickableLinkText";
import CommaSeparatedLinks from "@src/features/sidebars/ui/CommaSeparatedLinks";
import { format, parseISO } from "date-fns";
import Text from "@src/lib/ui/Text";
import Link from "next/link";
import { cn } from "@src/lib/utils/cn";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  HrisEmployeeFragment,
  useHrisEmployeesLazyQuery,
  useHrisEmployeesQuery,
} from "./HrisEmployeeListContainer.generated";
import { HrisEmployeesInput } from "@src/types.generated";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";
import { useToast } from "@src/lib/hooks/useToast";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import LinkHrisLocationDialog from "@src/features/settings/dialogs/LinkHrisLocationDialog";
import { DeleteHrisLocationDialog } from "./DeleteHrisLocationDialog";
import { Button } from "@src/lib/ui/Button";
import { useDeleteHrisLocationsMutation } from "./HrisLocationListContainer.generated";
import { GraphqlOperations } from "@src/types.generated";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import DotsIcon from "@src/lib/icons/18px/dots";
import LinkIcon from "@src/lib/icons/18px/link";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import { MapPinIcon } from "lucide-react";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";

type Props = {
  rowId: number;
  showHeader?: boolean;
};

const HrisLocationSidebar: React.FC<Props> = ({ rowId, showHeader = true }) => {
  const [linkLocationOpen, setLinkLocationOpen] = useState(false);
  const [deleteLocationOpen, setDeleteLocationOpen] = useState(false);
  const { addToast } = useToast();
  const { userActionPermissions } = useActionPermissions();
  const [deleteHrisLocations] = useDeleteHrisLocationsMutation({
    refetchQueries: [GraphqlOperations.Query.HrisLocations],
    onCompleted: () => {
      addToast({ message: "HRIS location deleted successfully" });
    },
  });

  const { data } = useHrisLocationSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });
  const inputFilters: HrisEmployeesInput = useMemo(() => {
    return {
      filters: {
        hrisLocationIds: { value: [rowId] },
        employmentStatus: { value: "ACTIVE" },
      },
    };
  }, [rowId]);

  // Fetch total count for the tab badge
  const { data: employeesCountData } = useHrisEmployeesQuery({
    variables: {
      input: inputFilters,
      pagination: { limit: 0 }, // Only need the count, not the data
    },
    skip: !rowId,
  });

  const columns = [
    "name",
    "hrisId",
    "employmentStatus",
    "opusUser",
    "location",
    "role",
    "syncedAt",
  ] as const;
  type Column = (typeof columns)[number];

  const tabCounts: QueryTabCounts<"details" | "employees"> = useMemo(() => {
    return {
      employees: employeesCountData?.HrisEmployees?.totalCount ?? undefined,
    };
  }, [employeesCountData]);

  const location = data?.HrisLocation;
  if (!location) return null;

  const tabKeys = ["details", "employees"] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    employees: "Active HRIS employees",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={items} />,
    employees: () => (
      <PaginatedTable<
        HrisEmployeeFragment,
        Column,
        typeof useHrisEmployeesQuery,
        typeof useHrisEmployeesLazyQuery,
        Column
      >
        defaultSort={{ column: "syncedAt", descending: true }}
        queryHook={useHrisEmployeesQuery}
        lazyQueryHook={useHrisEmployeesLazyQuery}
        getItems={(x) => x?.HrisEmployees?.objects ?? []}
        getTotalItemCount={(x) => x?.HrisEmployees?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={inputFilters}
        sidebarConfig={{
          tableId: "hris-location-employees",
          sidebarType: DetailSidebarType.HrisEmployee,
          enabled: true,
        }}
        disableRowClickOnColumns={new Set(["location", "role"])}
        allColumns={[...columns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["name"],
          defaultSelectedColumns: columns.filter(
            (col) => col !== "employmentStatus",
          ),
        }}
        columnTitles={{
          name: "Name",
          hrisId: "HRIS ID",
          employmentStatus: "HRIS Status",
          opusUser: "Opus user",
          location: "HRIS Location",
          role: "HRIS Role",
          syncedAt: "Synced from HRIS",
        }}
        renderFns={{
          name: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <UserOutlineIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {`${x.firstName} ${x.lastName}`}
              </Text>
            </div>
          ),
          location: (x) => (
            <CommaSeparatedLinks
              items={x.locationMemberships.map((memb) => ({
                id: memb.location.id,
                name: memb.location.name,
              }))}
              sidebarType={DetailSidebarType.HrisLocation}
              tableId="hris-locations"
            />
          ),
          role: (x) => (
            <CommaSeparatedLinks
              items={x.teamMemberships.map((memb) => ({
                id: memb.team.id,
                name: memb.team.name,
              }))}
              sidebarType={DetailSidebarType.HrisRole}
              tableId="hris-roles"
            />
          ),
          employmentStatus: (x) => {
            const status = x.employmentStatus || "--";
            const isActive = status === "ACTIVE";
            return (
              <Text
                variant="compact-description"
                className={isActive ? "" : "text-gray-7"}
              >
                {status}
              </Text>
            );
          },
          opusUser: (x) =>
            x.user?.name ? (
              <Link
                href={{
                  pathname: "/users/[id]",
                  query: {
                    id: x.user.id.toString(),
                  },
                }}
                className={cn("flex items-center no-underline text-[unset]")}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ClickableLinkText>{x.user.name}</ClickableLinkText>
              </Link>
            ) : (
              <Text variant="compact-description" className="text-gray-7">
                Not linked
              </Text>
            ),
          syncedAt: (x) => format(parseISO(x.createdAt), "M/d/yyyy"),
          hrisId: (x) => x.remoteHrisId,
        }}
      />
    ),
  };

  const items: InfoTableItem[] = [
    {
      label: "Opus location",
      value: location.group?.name ? (
        <Link
          href={{
            pathname: "/locations/[id]",
            query: {
              id: location.group.id.toString(),
            },
          }}
          className={cn("flex items-center no-underline text-[unset]")}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ClickableLinkText>{location.group.name}</ClickableLinkText>
        </Link>
      ) : (
        <Text variant="compact-description" className="text-gray-7">
          Not linked
        </Text>
      ),
    },
    {
      label: "Address",
      value: location.address
        ? [
            location.address.lineOne,
            location.address.lineTwo,
            [
              location.address.city,
              location.address.usState,
              location.address.postalCode,
            ]
              .filter(Boolean)
              .join(", "),
            location.address.country,
          ]
            .filter(Boolean)
            .join("\n")
        : "--",
    },
    {
      label: "Link method",
      value: location.group ? (
        location.manuallyLinked ? (
          "Manually linked"
        ) : (
          "Auto-linked"
        )
      ) : (
        <Text variant="compact-description" className="text-gray-7">
          Not linked
        </Text>
      ),
    },
    {
      label: "Active HRIS employees",
      value: `${location.activeHrisEmployeeCount} ${
        location.activeHrisEmployeeCount === 1 ? "employee" : "employees"
      }`,
    },
    {
      label: "HRIS integration",
      value: location.hrisIntegration?.id ? (
        <SidebarLink
          id={location.hrisIntegration.id}
          sidebarType={DetailSidebarType.HrisIntegration}
          tableId="hris-integrations-table"
        >
          {location.hrisIntegration.id}
        </SidebarLink>
      ) : (
        "--"
      ),
    } as InfoTableItem,
    {
      label: "First synced at",
      value: format(parseISO(location.createdAt), "M/d/yyyy h:mm a"),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={location.name}
        icon={<MapPinIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.HrisLocation}
      />
      <div className="px-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setLinkLocationOpen(true);
            }}
            disabled={!userActionPermissions?.changeOrgSettings}
          >
            <LinkIcon className="text-muted-foreground" />
            {location.group
              ? "Edit opus location link"
              : "Link to opus location"}
          </Button>
          {linkLocationOpen && (
            <LinkHrisLocationDialog
              open={linkLocationOpen}
              onOpenChange={setLinkLocationOpen}
              hrisLocation={location}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <DotsIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={!userActionPermissions?.changeOrgSettings}
                onClick={() => {
                  setDeleteLocationOpen(true);
                }}
              >
                <TrashOutlineIcon className="text-gray-10" />
                <Text variant="compact-title">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts as QueryTabCounts<TabKey>}
            defaultTab="details"
            queryParamKey="hris-location-tab"
          />
        </div>
      </SidebarContent>
      {deleteLocationOpen && location && (
        <DeleteHrisLocationDialog
          open={deleteLocationOpen}
          onOpenChange={setDeleteLocationOpen}
          locationName={location.name}
          onDelete={async () => {
            await deleteHrisLocations({
              variables: { ids: [location.id] },
            });
          }}
        />
      )}
    </div>
  );
};

// GraphQL query for the sidebar - fetches a single HRIS location by ID
gql`
  query HrisLocationSidebar($id: Int!) {
    HrisLocation(id: $id) {
      ...HrisLocation
      address {
        id
        lineOne
        lineTwo
        city
        usState
        country
        postalCode
      }
      manuallyLinked
      activeHrisEmployeeCount
      hrisIntegration {
        id
        hrisProvider
      }
    }
  }
`;

export default HrisLocationSidebar;
